import { useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

const PrivacyIos = () => {
  const [language, setLanguage] = useState<"en" | "fr">("en");

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          value={language}
          exclusive
          onChange={(_, nextLanguage) => {
            if (nextLanguage) {
              setLanguage(nextLanguage);
            }
          }}
        >
          <ToggleButton value="en">EN</ToggleButton>
          <ToggleButton value="fr">FR</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {language === "en" ? (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy (iOS App)
          </Typography>

          <Typography paragraph>Effective Date: 2026-01-01</Typography>
          <Typography paragraph>Last Updated: 2026-03-01</Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            L’API-L (“we”, “our”, “us”) operates the iOS mobile application
            "Dico API" (the “App”).
          </Typography>
          <Typography paragraph>
            We are committed to protecting the privacy of children, students,
            teachers, and all users of our phonetic dictionary application
            designed to support children with language learning difficulties,
            including dyslexia and allophone learners.
          </Typography>
          <Typography paragraph>
            This Privacy Policy explains how we handle information when you use
            our iOS App.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            2. No Personal Data Collection
          </Typography>
          <Typography paragraph>
            The iOS App does not collect, store, process, or share any personal
            data from users.
          </Typography>
          <Typography paragraph>Specifically:</Typography>
          <Typography paragraph>
            We do not require user registration.
          </Typography>
          <Typography paragraph>
            We do not collect names, email addresses, phone numbers, or contact
            information.
          </Typography>
          <Typography paragraph>
            We do not collect precise location data.
          </Typography>
          <Typography paragraph>
            We do not access the device camera, microphone, contacts, or photos.
          </Typography>
          <Typography paragraph>
            We do not track users across apps or websites.
          </Typography>
          <Typography paragraph>
            We do not use advertising identifiers (such as IDFA).
          </Typography>
          <Typography paragraph>
            We do not use third-party advertising SDKs.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            3. Educational Use and Children’s Privacy
          </Typography>
          <Typography paragraph>
            The App is designed for educational purposes and may be used by
            children under 13 years of age.
          </Typography>
          <Typography paragraph>
            Because the App does not collect personal data, it complies with
            applicable child data protection laws, including:
          </Typography>
          <Typography paragraph>
            The Children’s Online Privacy Protection Act (COPPA) in the United
            States
          </Typography>
          <Typography paragraph>
            The General Data Protection Regulation (GDPR) in the European Union
          </Typography>
          <Typography paragraph>
            The Swiss Federal Act on Data Protection (FADP)
          </Typography>
          <Typography paragraph>
            We do not knowingly collect any personal data from children or
            adults.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            4. Analytics and Technical Data
          </Typography>
          <Typography paragraph>
            The App does not use analytics tools that collect personal data.
          </Typography>
          <Typography paragraph>
            If anonymous technical diagnostics (such as crash reports) are
            implemented in the future to improve stability, they will:
          </Typography>
          <Typography paragraph>Not include personal data</Typography>
          <Typography paragraph>
            Not include identifiable user information
          </Typography>
          <Typography paragraph>
            Be used solely to improve app performance and reliability
          </Typography>
          <Typography paragraph>
            This Privacy Policy will be updated accordingly if such features are
            added.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            5. Data Storage
          </Typography>
          <Typography paragraph>
            The App does not store personal data locally on the device or on
            external servers.
          </Typography>
          <Typography paragraph>
            Any educational content accessed through the App is retrieved
            without associating it to an identifiable user.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            6. Third-Party Services
          </Typography>
          <Typography paragraph>
            The App does not integrate third-party services that collect
            personal data.
          </Typography>
          <Typography paragraph>
            If future versions integrate optional services, this Privacy Policy
            will be updated before release.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            7. Security
          </Typography>
          <Typography paragraph>
            Even though no personal data is collected, we implement reasonable
            technical measures to ensure secure communication between the App
            and our servers.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            8. Changes to This Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy if the App’s functionality
            changes.
          </Typography>
          <Typography paragraph>Any changes will be posted at:</Typography>
          <Typography paragraph>https://dicoapi.ch/privacy-ios</Typography>
          <Typography paragraph>
            The “Last Updated” date will reflect modifications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            9. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy, you may contact
            us at:
          </Typography>
          <Typography paragraph>L’API-L</Typography>
          <Typography paragraph>Email: contact@lapil.ch</Typography>
        </>
      ) : (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            Politique de confidentialité (Application iOS)
          </Typography>

          <Typography paragraph>
            Date d’entrée en vigueur : 2026-01-01
          </Typography>
          <Typography paragraph>Dernière mise à jour : 2026-03-01</Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            L’API-L (« nous », « notre », « nos ») exploite l’application mobile
            iOS « Dico API » (l’« App »).
          </Typography>
          <Typography paragraph>
            Nous nous engageons à protéger la vie privée des enfants, des
            élèves, des enseignant·e·s et de l’ensemble des utilisateurs de
            notre dictionnaire phonétique conçu pour soutenir les enfants ayant
            des difficultés d’apprentissage du langage, y compris la dyslexie et
            les apprenants allophones.
          </Typography>
          <Typography paragraph>
            Cette politique de confidentialité explique comment nous traitons
            les informations lorsque vous utilisez notre application iOS.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            2. Aucune collecte de données personnelles
          </Typography>
          <Typography paragraph>
            L’application iOS ne collecte, ne stocke, ne traite et ne partage
            aucune donnée personnelle des utilisateurs.
          </Typography>
          <Typography paragraph>Plus précisément :</Typography>
          <Typography paragraph>
            Nous n’exigeons aucune inscription utilisateur.
          </Typography>
          <Typography paragraph>
            Nous ne collectons pas de noms, d’adresses e-mail, de numéros de
            téléphone ni d’informations de contact.
          </Typography>
          <Typography paragraph>
            Nous ne collectons pas de données de localisation précise.
          </Typography>
          <Typography paragraph>
            Nous n’accédons pas à la caméra, au microphone, aux contacts ni aux
            photos de l’appareil.
          </Typography>
          <Typography paragraph>
            Nous ne suivons pas les utilisateurs à travers des applications ou
            des sites web.
          </Typography>
          <Typography paragraph>
            Nous n’utilisons pas d’identifiants publicitaires (tels que l’IDFA).
          </Typography>
          <Typography paragraph>
            Nous n’utilisons pas de SDK publicitaires tiers.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            3. Usage éducatif et confidentialité des enfants
          </Typography>
          <Typography paragraph>
            L’application est conçue à des fins éducatives et peut être utilisée
            par des enfants de moins de 13 ans.
          </Typography>
          <Typography paragraph>
            Comme l’application ne collecte aucune donnée personnelle, elle est
            conforme aux lois applicables en matière de protection des données
            des enfants, notamment :
          </Typography>
          <Typography paragraph>
            Le Children’s Online Privacy Protection Act (COPPA) aux États-Unis
          </Typography>
          <Typography paragraph>
            Le Règlement général sur la protection des données (RGPD) dans
            l’Union européenne
          </Typography>
          <Typography paragraph>
            La Loi fédérale suisse sur la protection des données (LPD)
          </Typography>
          <Typography paragraph>
            Nous ne collectons sciemment aucune donnée personnelle d’enfants ou
            d’adultes.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            4. Données analytiques et techniques
          </Typography>
          <Typography paragraph>
            L’application n’utilise pas d’outils analytiques collectant des
            données personnelles.
          </Typography>
          <Typography paragraph>
            Si des diagnostics techniques anonymes (comme des rapports de crash)
            sont mis en place à l’avenir pour améliorer la stabilité, ils :
          </Typography>
          <Typography paragraph>
            N’incluront pas de données personnelles
          </Typography>
          <Typography paragraph>
            N’incluront pas d’informations permettant d’identifier un
            utilisateur
          </Typography>
          <Typography paragraph>
            Seront utilisés uniquement pour améliorer les performances et la
            fiabilité de l’application
          </Typography>
          <Typography paragraph>
            Cette politique de confidentialité sera mise à jour en conséquence
            si de telles fonctionnalités sont ajoutées.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            5. Stockage des données
          </Typography>
          <Typography paragraph>
            L’application ne stocke aucune donnée personnelle localement sur
            l’appareil ni sur des serveurs externes.
          </Typography>
          <Typography paragraph>
            Tout contenu éducatif accessible via l’application est récupéré sans
            être associé à un utilisateur identifiable.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            6. Services tiers
          </Typography>
          <Typography paragraph>
            L’application n’intègre pas de services tiers qui collectent des
            données personnelles.
          </Typography>
          <Typography paragraph>
            Si des versions futures intègrent des services optionnels, cette
            politique de confidentialité sera mise à jour avant leur
            publication.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            7. Sécurité
          </Typography>
          <Typography paragraph>
            Même si aucune donnée personnelle n’est collectée, nous mettons en
            œuvre des mesures techniques raisonnables pour garantir une
            communication sécurisée entre l’application et nos serveurs.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            8. Modifications de cette politique
          </Typography>
          <Typography paragraph>
            Nous pouvons mettre à jour cette politique de confidentialité si les
            fonctionnalités de l’application évoluent.
          </Typography>
          <Typography paragraph>
            Toute modification sera publiée à l’adresse :
          </Typography>
          <Typography paragraph>https://dicoapi.ch/privacy-ios</Typography>
          <Typography paragraph>
            La date de « Dernière mise à jour » reflète les modifications.
          </Typography>

          <Typography variant="h6" component="h2" gutterBottom>
            9. Contact
          </Typography>
          <Typography paragraph>
            Si vous avez des questions concernant cette politique de
            confidentialité, vous pouvez nous contacter à :
          </Typography>
          <Typography paragraph>L’API-L</Typography>
          <Typography paragraph>Email : contact@lapil.ch</Typography>
        </>
      )}
    </Box>
  );
};

export default PrivacyIos;
